import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import SmartToyOutlinedIcon from "@mui/icons-material/SmartToyOutlined";
import CloseIcon from "@mui/icons-material/Close";
import SendIcon from "@mui/icons-material/Send";
import useSectionDarkMode from "../../hooks/useSectionDarkMode";
import { getOrCreateSessionId, sendChatMessage } from "../../service/chatService";

const QUICK_REPLY_KEYS = ["q1", "q2", "q3"];
const TYPEWRITER_MS = 18;
/** Tạm ẩn pill link nguồn (Learn more / Portal…) — URL vẫn clickable trong nội dung câu trả lời. */
const SHOW_SUGGESTED_SOURCE_LINKS = false;

const URL_IN_TEXT_REGEX =
  /([a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,})|(https?:\/\/[^\s<>"']+)|((?:www\.)?(?:agent\.quantera\.vn|gopaperless\.quantera\.vn|quantera\.vn|trustedbilling\.mobile-id\.vn)\/[^\s<>"']*)/gi;

function trimTrailingUrlPunctuation(url) {
  return url.replace(/[),.!?;:]+$/, "");
}

function toHref(raw, kind) {
  const cleaned = trimTrailingUrlPunctuation(raw);
  if (kind === "email") {
    return `mailto:${cleaned}`;
  }
  if (/^https?:\/\//i.test(cleaned)) {
    return cleaned;
  }
  return `https://${cleaned.replace(/^\/\//, "")}`;
}

/**
 * Email + URL https + domain Quantera có path → hyperlink.
 * Không gắn domain trần (quantera.vn) trong câu giới thiệu; không cắt info@quantera.vn.
 */
function LinkifyText({ text, className = "" }) {
  if (!text) return null;

  const parts = [];
  let lastIndex = 0;
  let match;

  const re = new RegExp(URL_IN_TEXT_REGEX.source, "gi");
  while ((match = re.exec(text)) !== null) {
    const raw = match[0];
    const kind = match[1] ? "email" : "url";

    // Phòng hờ: domain đứng sau @ thì bỏ qua
    if (match.index > 0 && text[match.index - 1] === "@") {
      continue;
    }

    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }
    const href = toHref(raw, kind);
    parts.push(
      <a
        key={`${match.index}-${href}`}
        href={href}
        target={kind === "email" ? undefined : "_blank"}
        rel={kind === "email" ? undefined : "noopener noreferrer"}
        className="text-[#7EC4FF] underline underline-offset-2 hover:text-[#A8D8FF] break-all font-medium"
      >
        {trimTrailingUrlPunctuation(raw)}
      </a>
    );
    lastIndex = match.index + raw.length;
  }

  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }

  if (parts.length === 0) {
    return text;
  }

  return <span className={className}>{parts}</span>;
}

function sourceLabel(url) {
  const u = (url || "").toLowerCase();
  if (u.includes("/qa")) return "Portal QA";
  if (u.includes("digital-signature")) return "Digital Signature";
  if (u.includes("pricing/portal") || (u.includes("gopaperless") && u.includes("pricing"))) return "Portal Pricing";
  if (u.includes("features-for-teams")) return "Portal Features";
  if (u.includes("compliance")) return "Compliance";
  if (u.includes("solutions")) return "Solutions";
  if (u.includes("/installation") || u.includes("install-gopaperless")) return "Agent Install";
  if (u.includes("/userguide")) return "Agent Guide";
  if (u.includes("/cases/")) return "Agent Use Case";
  if (u.includes("agent.quantera") && u.includes("pricing")) return "Agent Pricing";
  if (u.includes("invoice-inquiry")) return "Invoice Inquiry";
  if (u.includes("agent.quantera.vn")) return "GoPaperless Agent";
  if (u.includes("gopaperless.quantera.vn")) return "GoPaperless Portal";
  if (u.includes("trustedbilling")) return "Trusted Billing";
  if (u.includes("quantera.vn")) return "Quantera";
  return "Learn more";
}

/** Hiện dần từng ký tự — chỉ dùng cho tin bot mới nhất chưa hoàn tất. */
function TypewriterText({ text, speed = TYPEWRITER_MS, onDone, onTick }) {
  const [shown, setShown] = useState("");
  const doneRef = useRef(false);
  const onDoneRef = useRef(onDone);
  const onTickRef = useRef(onTick);

  useEffect(() => {
    onDoneRef.current = onDone;
    onTickRef.current = onTick;
  }, [onDone, onTick]);

  useEffect(() => {
    doneRef.current = false;
    setShown("");
    if (!text) {
      doneRef.current = true;
      onDoneRef.current?.();
      return undefined;
    }

    let index = 0;
    const timer = window.setInterval(() => {
      index += 1;
      setShown(text.slice(0, index));
      onTickRef.current?.();
      if (index >= text.length) {
        window.clearInterval(timer);
        if (!doneRef.current) {
          doneRef.current = true;
          onDoneRef.current?.();
        }
      }
    }, speed);

    return () => window.clearInterval(timer);
  }, [text, speed]);

  return (
    <>
      {shown}
      {shown.length < (text?.length || 0) && (
        <span className="inline-block w-[2px] h-[1em] ml-0.5 align-[-0.1em] bg-current animate-pulse" />
      )}
    </>
  );
}

function ThinkingIndicator({ label }) {
  return (
    <div className="flex justify-start">
      <div
        className="max-w-[85%] min-w-0 rounded-2xl rounded-bl-md px-3 py-2.5 bg-white/10 text-neutral-1"
        role="status"
        aria-live="polite"
        aria-label={label}
      >
        <div className="flex items-center gap-1.5 h-5">
          <span className="chat-thinking-dot" />
          <span className="chat-thinking-dot" />
          <span className="chat-thinking-dot" />
        </div>
      </div>
    </div>
  );
}

export default function ChatWidget({ lang }) {
  const { t } = useTranslation();
  const darkMode = useSectionDarkMode("bottom");
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [messages, setMessages] = useState([]);
  const [typingDoneIds, setTypingDoneIds] = useState(() => new Set());
  const bottomRef = useRef(null);
  const msgIdRef = useRef(0);

  const nextMsgId = () => {
    msgIdRef.current += 1;
    return `msg-${msgIdRef.current}`;
  };

  const scrollToBottom = () => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    if (open && messages.length === 0) {
      setMessages([
        {
          id: nextMsgId(),
          role: "bot",
          text: t("chat.welcome"),
          fallbackContact: false,
          typing: true,
        },
      ]);
    }
  }, [open, messages.length, t]);

  useEffect(() => {
    scrollToBottom();
  }, [messages, loading, open, typingDoneIds]);

  /** Đánh dấu typing xong và tắt cờ typing để đóng/mở lại không animate lại tin cũ. */
  const markTypingDone = (id) => {
    setTypingDoneIds((prev) => {
      if (prev.has(id)) return prev;
      const next = new Set(prev);
      next.add(id);
      return next;
    });
    setMessages((prev) =>
      prev.map((m) => (m.id === id && m.typing ? { ...m, typing: false } : m))
    );
  };

  const finishPendingTyping = () => {
    setMessages((prev) => prev.map((m) => (m.typing ? { ...m, typing: false } : m)));
    setTypingDoneIds((prev) => {
      const next = new Set(prev);
      messages.forEach((m) => {
        if (m.role === "bot") next.add(m.id);
      });
      return next;
    });
  };

  const closeChat = () => {
    finishPendingTyping();
    setOpen(false);
  };

  const scrollToContact = () => {
    const el = document.getElementById("contact");
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
    closeChat();
  };

  const hasPendingBotTyping = () =>
    messages.some((m) => m.role === "bot" && m.typing && !typingDoneIds.has(m.id));

  const handleSend = async (text) => {
    const question = (text ?? input).trim();
    // Chặn gửi khi đang chờ API hoặc typewriter bot chưa xong
    if (!question || loading || hasPendingBotTyping()) return;

    setInput("");
    const lastBotAnswer = [...messages].reverse().find((m) => m.role === "bot")?.text ?? null;
    setMessages((prev) => [...prev, { id: nextMsgId(), role: "user", text: question }]);
    setLoading(true);

    try {
      const data = await sendChatMessage({
        message: question,
        locale: lang,
        sessionId: getOrCreateSessionId(),
        lastBotAnswer,
      });

      setMessages((prev) => [
        ...prev,
        {
          id: nextMsgId(),
          role: "bot",
          text: data?.answer || t("chat.error"),
          fallbackContact: Boolean(data?.fallbackContact),
          sources: data?.sources || [],
          suggestedActions: data?.suggestedActions || [],
          suggestedReplies: data?.suggestedReplies || [],
          typing: true,
        },
      ]);
    } catch (error) {
      console.error(error);
      setMessages((prev) => [
        ...prev,
        {
          id: nextMsgId(),
          role: "bot",
          text: t("chat.error"),
          fallbackContact: true,
          typing: true,
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  // Chỉ tin bot mới nhất còn typing mới chạy typewriter
  const latestTypingId = (() => {
    for (let i = messages.length - 1; i >= 0; i -= 1) {
      const m = messages[i];
      if (m.role === "bot" && m.typing && !typingDoneIds.has(m.id)) {
        return m.id;
      }
    }
    return null;
  })();

  const isBusy = loading || latestTypingId != null;

  const floatingBtnClass = `
    fixed right-5 z-[900]
    w-14 h-14 rounded-full
    flex items-center justify-center
    backdrop-blur-[30px]
    transition-all duration-300
    shadow-lg hover:scale-105 active:scale-95
    ${darkMode ? "bg-white/20 text-neutral-13" : "bg-white/20 text-neutral-1"}
  `;

  const floatingIconClass = `
    text-[26px]
    transition-colors
    ${darkMode ? "text-black" : "text-white"}
  `;

  const defaultQuickReplies = QUICK_REPLY_KEYS.map((key) => t(`chat.quickReplies.${key}`));
  const lastBot = [...messages].reverse().find((m) => m.role === "bot");
  const quickReplies = lastBot?.suggestedReplies?.length
    ? lastBot.suggestedReplies.slice(0, 3)
    : defaultQuickReplies;

  return (
    <>
      {open && (
        <div
          style={{ bottom: "calc(168px + var(--cookie-banner-h, 0px))" }}
          className="
            fixed right-5 z-[950]
            w-[min(100vw-2rem,380px)] h-[min(70vh,520px)]
            rounded-2xl shadow-2xl overflow-hidden
            flex flex-col
            bg-neutral-13/95 backdrop-blur-xl border border-white/10
            transition-[bottom] duration-300
          "
        >
          <div className="flex items-center justify-between px-4 py-3 bg-gradient-to-r from-[#036AE5] to-[#58F8FF]">
            <div>
              <p className="text-white font-semibold text-[16px]">{t("chat.title")}</p>
              <p className="text-white/80 text-[12px]">{t("chat.subtitle")}</p>
            </div>
            <button
              type="button"
              onClick={closeChat}
              aria-label={t("chat.close")}
              className="text-white hover:opacity-80"
            >
              <CloseIcon />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto overflow-x-hidden px-4 py-3 space-y-3">
            {messages.map((msg) => {
              const isAnimating = msg.id === latestTypingId;
              const showExtras = msg.role === "bot" && !isAnimating;

              return (
              <div
                key={msg.id}
                className={`flex min-w-0 ${msg.role === "user" ? "justify-end" : "justify-start"}`}
              >
                <div
                  className={`
                    max-w-[85%] min-w-0 rounded-2xl px-3 py-2 text-[14px] leading-6
                    whitespace-pre-wrap break-words [overflow-wrap:anywhere]
                    ${msg.role === "user"
                      ? "bg-brandBlue-500 text-white rounded-br-md"
                      : "bg-white/10 text-neutral-1 rounded-bl-md"}
                  `}
                >
                  <div className="min-w-0 max-w-full overflow-hidden">
                    {isAnimating ? (
                      <TypewriterText
                        text={msg.text}
                        onTick={scrollToBottom}
                        onDone={() => markTypingDone(msg.id)}
                      />
                    ) : (
                      <LinkifyText text={msg.text} />
                    )}
                  </div>
                  {SHOW_SUGGESTED_SOURCE_LINKS && showExtras && msg.sources?.length > 0 && (
                    <div className="mt-3 flex flex-wrap gap-2 min-w-0">
                      {msg.sources.map((url) => (
                        <a
                          key={url}
                          href={url}
                          target="_blank"
                          rel="noopener noreferrer"
                          title={url}
                          className="max-w-full truncate px-3 py-1 rounded-full text-[12px] bg-white/15 hover:bg-white/25"
                        >
                          {sourceLabel(url)}
                        </a>
                      ))}
                    </div>
                  )}
                  {showExtras && (msg.fallbackContact || msg.suggestedActions?.includes("contact") || msg.suggestedActions?.includes("call")) && (
                    <div className="mt-3 flex flex-wrap gap-2">
                      {(msg.fallbackContact || msg.suggestedActions?.includes("contact")) && (
                        <button
                          type="button"
                          onClick={scrollToContact}
                          className="px-3 py-1 rounded-full text-[12px] bg-white/15 hover:bg-white/25"
                        >
                          {t("chat.contact")}
                        </button>
                      )}
                      {(msg.fallbackContact || msg.suggestedActions?.includes("call")) && (
                        <a
                          href="tel:19006884"
                          className="px-3 py-1 rounded-full text-[12px] bg-white/15 hover:bg-white/25"
                        >
                          {t("chat.call")}
                        </a>
                      )}
                    </div>
                  )}
                </div>
              </div>
              );
            })}

            {loading && <ThinkingIndicator label={t("chat.thinking")} />}
            <div ref={bottomRef} />
          </div>

          <div className="px-3 pt-2 pb-1 flex flex-wrap gap-2">
            {quickReplies.map((label) => (
              <button
                key={label}
                type="button"
                onClick={() => handleSend(label)}
                disabled={isBusy}
                className="text-[11px] px-2 py-1 rounded-full bg-white/10 hover:bg-white/20 text-neutral-2 disabled:opacity-40 disabled:pointer-events-none"
              >
                {label}
              </button>
            ))}
          </div>

          <form
            className="p-3 border-t border-white/10 flex gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              if (!isBusy) handleSend();
            }}
          >
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={t("chat.placeholder")}
              maxLength={500}
              disabled={isBusy}
              className="
                flex-1 rounded-xl px-3 py-2 text-[14px]
                bg-white/10 text-neutral-1 placeholder:text-neutral-4
                outline-none focus:ring-2 focus:ring-brandBlue-500/50
                disabled:opacity-50 disabled:cursor-not-allowed
              "
            />
            <button
              type="submit"
              disabled={isBusy || !input.trim()}
              aria-label={t("chat.send")}
              className="
                w-10 h-10 rounded-xl flex items-center justify-center
                bg-brandBlue-500 text-white
                disabled:opacity-40 hover:opacity-90
              "
            >
              <SendIcon fontSize="small" />
            </button>
          </form>
        </div>
      )}

      <button
        type="button"
        onClick={() => {
          if (open) {
            closeChat();
          } else {
            setOpen(true);
          }
        }}
        aria-label={t("chat.open")}
        style={{ bottom: "calc(20px + var(--cookie-banner-h, 0px))" }}
        className={floatingBtnClass}
      >
        {open ? (
          <CloseIcon className={floatingIconClass} />
        ) : (
          <SmartToyOutlinedIcon className={floatingIconClass} />
        )}
      </button>
    </>
  );
}
